import {
  Alert,
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography
} from '@mui/material'
import React, {
  type ChangeEvent,
  useCallback,
  useEffect,
  useState
} from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'

import { MonitorUrlFieldInput } from 'components/status-checks/form/status-check/common/MonitorUrlFieldInput'
import { StatusCheckNameField } from 'components/status-checks/form/status-check/common/StatusCheckNameField'
import { GrafanaInstanceFilters } from 'components/status-checks/form/status-check/grafana/GrafanaInstanceFilters'
import { GrafanaMonitorSearch } from 'components/status-checks/form/status-check/grafana/GrafanaMonitorSearch'
import { useStatusCheckDetailsContext } from 'context/status-checks/StatusCheckDetailsContext'
import { useFeatureFlags } from 'hooks/use-feature-flags'
import {
  splitGrafanaAlertsUrl,
  splitSilencedParam
} from 'utils/status-checks/grafana-instance-filters'
import {
  findAlertIdInUrl,
  GRAFANA_MONITOR_URL_PREFIX
} from 'utils/status-checks/status-check-integrations-util-grafana'

import type { StatusCheckFormValues } from 'components/status-checks/status-checks-form-utils'

interface MovedUrlOptions {
  filterCount: number
  haltWhenSilenced: boolean | undefined
  silencedParam: string | null
}

export enum GrafanaSelectionSource {
  SEARCH = 'search',
  ALERT_URL = 'alert_url'
}

const fieldsToWatch = [
  'externalIntegration',
  'grafanaSelector.type',
  'monitorUrl',
  'grafanaSelector.filters',
  'grafanaSelector.haltWhenSilenced'
] as const

export const GrafanaMonitorSelection: React.FC = () => {
  const { serviceName } = useStatusCheckDetailsContext()
  const { control, getValues, setValue, trigger } =
    useFormContext<StatusCheckFormValues>()
  const { resetEndpointEvaluationResponse } = useStatusCheckDetailsContext()
  const [selectedAlertId, setSelectedAlertId] = useState<string>()
  const [movedUrlOptions, setMovedUrlOptions] = useState<MovedUrlOptions>()
  const { grafanaInstanceFilters: isInstanceFiltersEnabled } = useFeatureFlags()

  const [
    externalIntegration,
    grafanaSelectorType,
    monitorUrl,
    grafanaFilters,
    haltWhenSilenced
  ] = useWatch({
    control,
    name: fieldsToWatch
  })

  const handleRadioOptionChange = useCallback(
    (_event: ChangeEvent<HTMLInputElement>, value: GrafanaSelectionSource) => {
      setValue('grafanaSelector.type', value, {
        shouldDirty: true
      })
      setMovedUrlOptions(undefined)
    },
    [setValue]
  )

  useEffect(() => {
    resetEndpointEvaluationResponse()
  }, [
    grafanaSelectorType,
    monitorUrl,
    grafanaFilters,
    haltWhenSilenced,
    resetEndpointEvaluationResponse
  ])

  // Waits for blur or Enter so a half-typed param isn't pulled out from under the cursor
  function moveUrlOptionsToControls() {
    if (!isInstanceFiltersEnabled) return

    const { silenced, url: urlWithoutSilenced } = splitSilencedParam(
      getValues('monitorUrl')
    )
    const { filters: urlFilters, url } =
      splitGrafanaAlertsUrl(urlWithoutSilenced)

    if ((silenced === null && !urlFilters.length) || !findAlertIdInUrl(url)) {
      setMovedUrlOptions(undefined)
      return
    }

    setValue('monitorUrl', url, { shouldDirty: true, shouldValidate: true })
    if (urlFilters.length) {
      setValue(
        'grafanaSelector.filters',
        [...getValues('grafanaSelector.filters'), ...urlFilters],
        { shouldDirty: true }
      )
    }
    const urlHaltWhenSilenced =
      silenced === null ? undefined : silenced !== 'false'
    if (urlHaltWhenSilenced !== undefined) {
      setValue('grafanaSelector.haltWhenSilenced', urlHaltWhenSilenced, {
        shouldDirty: true
      })
    }
    setMovedUrlOptions({
      filterCount: urlFilters.length,
      haltWhenSilenced: urlHaltWhenSilenced,
      silencedParam: silenced
    })
  }

  const isSilencedNoteCurrent =
    movedUrlOptions?.haltWhenSilenced !== undefined &&
    movedUrlOptions.haltWhenSilenced === haltWhenSilenced
  const movedFilterCount = movedUrlOptions?.filterCount ?? 0

  // Alert options are part of the duplicate check, so monitorUrl's error depends on them
  useEffect(() => {
    if (getValues('monitorUrl')) void trigger('monitorUrl')
  }, [grafanaFilters, haltWhenSilenced, getValues, trigger])

  return (
    <Stack spacing={4}>
      <StatusCheckNameField />
      <Stack spacing={2}>
        <Typography>
          What monitor or alert will help Gremlin determine the health of your
          Service?
        </Typography>
        <Controller
          control={control}
          name="grafanaSelector.type"
          render={({ field }) => {
            return (
              <RadioGroup
                {...field}
                value={grafanaSelectorType}
                onChange={handleRadioOptionChange}
              >
                <FormControl sx={{ mb: 2 }}>
                  <Stack spacing={1}>
                    <FormControlLabel
                      control={<Radio value={GrafanaSelectionSource.SEARCH} />}
                      label="Search for Grafana Alert by name or tags"
                    />
                    <Box sx={{ pl: 4 }}>
                      <GrafanaMonitorSearch
                        disabled={
                          grafanaSelectorType !== GrafanaSelectionSource.SEARCH
                        }
                        initialSearchValue={serviceName}
                        selectedValue={selectedAlertId}
                        onSelect={async (alertId) => {
                          if (!externalIntegration) return

                          setSelectedAlertId(alertId)
                          setValue(
                            'monitorUrl',
                            `${externalIntegration.url}${GRAFANA_MONITOR_URL_PREFIX}${alertId}`,
                            { shouldDirty: true }
                          )
                          await trigger('monitorUrl')
                        }}
                      />
                    </Box>
                  </Stack>
                </FormControl>
                <FormControl>
                  <Stack spacing={1}>
                    <FormControlLabel
                      control={
                        <Radio value={GrafanaSelectionSource.ALERT_URL} />
                      }
                      label="Provide Grafana Alert URL"
                    />
                    <Controller
                      control={control}
                      name="monitorUrl"
                      render={({ field, fieldState }) => (
                        <Box sx={{ pl: 4, width: '100%' }}>
                          <MonitorUrlFieldInput
                            disabled={
                              grafanaSelectorType !==
                              GrafanaSelectionSource.ALERT_URL
                            }
                            externalIntegration={externalIntegration}
                            field={field}
                            fieldState={fieldState}
                            label="Grafana Alert URL"
                            onBlur={() => {
                              field.onBlur()
                              moveUrlOptionsToControls()
                            }}
                            onKeyDown={(event) => {
                              if (
                                event.key === 'Enter' &&
                                !event.nativeEvent.isComposing
                              ) {
                                moveUrlOptionsToControls()
                              }
                            }}
                          />
                        </Box>
                      )}
                    />
                  </Stack>
                </FormControl>
              </RadioGroup>
            )
          }}
        />
      </Stack>
      {(isSilencedNoteCurrent || movedFilterCount > 0) && (
        <Alert severity="info" onClose={() => setMovedUrlOptions(undefined)}>
          {isSilencedNoteCurrent && (
            <div>
              This URL {haltWhenSilenced ? 'includes' : 'leaves out'} silenced
              alerts. We removed silenced={movedUrlOptions?.silencedParam} from
              it and {haltWhenSilenced ? 'checked' : 'unchecked'} &quot;Still
              halt when this alert is silenced&quot; instead.
            </div>
          )}
          {movedFilterCount > 0 && (
            <div>
              We moved {movedFilterCount} filter
              {movedFilterCount === 1 ? '' : 's'} from the URL into the filters
              below.
            </div>
          )}
        </Alert>
      )}
      {isInstanceFiltersEnabled && <GrafanaInstanceFilters />}
    </Stack>
  )
}
